import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { map } from 'lodash';
import pluralize from 'pluralize';
import React, { FunctionComponent, useState } from 'react';

import { DialogChromeFooter, DialogChromeHeader, dialogPrimaryActionSx } from 'components/atoms/Dialog/DialogChrome';

import {
  CaseOrganizationExecutionMode,
  caseOrganizationHandlingLabels,
  CaseOrganizationPlan,
} from './caseOrganizationPlan';

export const WHOLE_STUDY_CONFIRMATION_TEXT = 'REPLACE WHOLE STUDY';

interface CaseOrganizationConfirmationDialogProps {
  open: boolean;
  plan: CaseOrganizationPlan;
  onClose: () => void;
  onApplyCaseOrganization: () => void;
}

const getReviewTitle = (executionMode: CaseOrganizationExecutionMode) => {
  if (executionMode === 'wholeStudyReplacement') {
    return 'Review study case changes';
  }

  if (executionMode === 'selectedCaseOverride') {
    return 'Review cases in the plan';
  }

  return 'Review slide organization';
};

const getPrimaryActionLabel = (executionMode: CaseOrganizationExecutionMode) => {
  if (executionMode === 'wholeStudyReplacement') {
    return 'Replace study case assignments';
  }

  if (executionMode === 'selectedCaseOverride') {
    return 'Apply case changes';
  }

  return 'Save changes';
};

const PlanSummary: FunctionComponent<{ plan: CaseOrganizationPlan }> = ({ plan }) => {
  const summaryItems = [
    { noun: 'Case', action: 'created', count: plan.createdCases.count },
    { noun: 'Case', action: 'updated', count: plan.updatedCases.count },
    { noun: 'Slide', action: 'assigned', count: plan.assignedSlides.count },
    { noun: 'Slide', action: 'moved', count: plan.movedSlides.count },
    { noun: 'Case', action: 'to be deleted', count: plan.emptiedCases.count },
    { noun: 'Slide', action: 'from another study or lab', count: plan.crossScopeSlides.count },
    { noun: 'Case', action: 'to be removed', count: plan.replacedCases.count },
  ].filter(({ count }) => count > 0);

  if (plan.isNoOp) {
    return <Alert severity="info">No changes are available to apply.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <List dense disablePadding aria-label="Changes to apply">
        {map(summaryItems, ({ noun, action, count }) => (
          <ListItem key={`${noun}-${action}`} disableGutters>
            <ListItemText primary={`${count} ${pluralize(noun, count)} ${action}`} />
          </ListItem>
        ))}
      </List>
      {plan.movedSlides.count > 0 ? (
        <Box>
          <Typography variant="h3" sx={{ mb: 1 }}>
            Slides changing cases
          </Typography>
          <DataGrid
            rows={plan.movedSlides.items}
            getRowId={(row) => row.slideId}
            columns={[
              { field: 'slideLabel', headerName: 'Slide', flex: 1 },
              { field: 'sourceCaseLabel', headerName: 'Current case', flex: 1 },
              { field: 'targetCaseLabel', headerName: 'New case', flex: 1 },
            ]}
            autoHeight
            disableRowSelectionOnClick
            hideFooter
          />
          {plan.movedSlides.omittedCount > 0 ? (
            <Typography variant="caption" color="text.secondary">
              {`${plan.movedSlides.omittedCount} additional ${pluralize(
                'slide',
                plan.movedSlides.omittedCount
              )} will change cases.`}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {plan.emptiedCases.count > 0 ? (
        <Alert severity="warning">
          {`${plan.emptiedCases.count} ${pluralize(
            'case',
            plan.emptiedCases.count
          )} will be deleted because no slides will remain.`}
        </Alert>
      ) : null}
    </Stack>
  );
};

export const CaseOrganizationConfirmationDialog: FunctionComponent<
  React.PropsWithChildren<CaseOrganizationConfirmationDialogProps>
> = ({ open, plan, onClose, onApplyCaseOrganization }) => {
  const [wholeStudyConfirmation, setWholeStudyConfirmation] = useState('');
  const executionMode = plan.executionMode;
  const activePlan = plan;
  const requiresTypedConfirmation = executionMode === 'wholeStudyReplacement';
  const primaryActionDisabled =
    activePlan.isNoOp || (requiresTypedConfirmation && wholeStudyConfirmation !== WHOLE_STUDY_CONFIRMATION_TEXT);

  const resetAndClose = () => {
    setWholeStudyConfirmation('');
    onClose();
  };

  const applyCaseOrganization = () => {
    resetAndClose();
    onApplyCaseOrganization();
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="case-organization-review-title"
    >
      <DialogChromeHeader
        title={getReviewTitle(executionMode)}
        subtitle="Review these changes before saving."
        titleId="case-organization-review-title"
        closeButtonAriaLabel="Close slide organization review"
        onClose={resetAndClose}
      />
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Typography variant="body2">
            {executionMode === 'selectedCaseOverride'
              ? 'For cases in the plan, keep only the slides listed there.'
              : caseOrganizationHandlingLabels[executionMode]}
          </Typography>
          <PlanSummary plan={activePlan} />
          {executionMode === 'selectedCaseOverride' ? (
            <Alert severity="info">
              Cases not listed in the plan are kept, except for slides moved out of them. A case is deleted if all its
              slides are moved elsewhere.
            </Alert>
          ) : null}
          {executionMode === 'wholeStudyReplacement' ? (
            <Stack spacing={2}>
              <Alert severity="warning">
                {`This will delete ${activePlan.replacedCases.count} ${pluralize(
                  'case',
                  activePlan.replacedCases.count
                )} not included in the plan, containing ${activePlan.replacedSlidesCount} ${pluralize(
                  'slide',
                  activePlan.replacedSlidesCount
                )}. Cases in the plan will be created or updated. Deletion and case updates are applied separately, so a failure may leave partial changes.`}
              </Alert>
              <TextField
                fullWidth
                label={`Type ${WHOLE_STUDY_CONFIRMATION_TEXT} to confirm`}
                value={wholeStudyConfirmation}
                onChange={(event) => setWholeStudyConfirmation(event.target.value)}
                autoComplete="off"
              />
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogChromeFooter>
        <Button color="inherit" onClick={resetAndClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={executionMode === 'wholeStudyReplacement' ? 'error' : 'primary'}
          sx={dialogPrimaryActionSx}
          disabled={primaryActionDisabled}
          onClick={applyCaseOrganization}
        >
          {getPrimaryActionLabel(executionMode)}
        </Button>
      </DialogChromeFooter>
    </Dialog>
  );
};
